import React, { createContext, useContext } from 'react'

import PromoPricingInfo from '../models/promoPricingInfo'
import native from '../native/native'
import { isPromo } from '../utils/utils'
import { useAppState } from './appStateContext'
import useQueryParams from '../hooks/useQueryParams'

interface State {
  premiumPricing: PromoPricingInfo
  ultimatePricing: PromoPricingInfo
  currentPricing: PromoPricingInfo
  isUltimate: boolean
  isLapsed: boolean
  isPremiumUser: boolean
  isUltimateLaunch: boolean
}

interface Actions {
  openPaywallFromBanner: () => void
  purchaseYearly: () => void
  purchaseMonthly: () => void
}

interface Context {
  state: State
  actions: Actions
}

const BlackFridayContext = createContext<Context | null>(null)

const BlackFridayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const productData = useAppState().state.productData!

  const openPaywallFromBanner = () => {
    native.postNativeEvent("subscribe")
  }

  const purchaseYearly = () => {
    if (isDiscountPurchase && currentPlan.yearlyProduct.discounts.length > 0) {
      native.subscribeWithDiscount(currentPlan.yearlyProduct)
    } else {
      native.subscribe(currentPlan.yearlyProduct)
    }
  }

  const purchaseMonthly = () => {
    native.subscribe(currentPlan.monthlyProduct)
  }

  const queryParams = useQueryParams()
  const isUltimate = queryParams.isUltimate;
  const isPremiumUser = queryParams.isPremiumUser;
  const isLapsed = queryParams.isLapsed;
  const isUltimateLaunch = queryParams.isUltimateLaunch;
  const useYearlyForDiscount = queryParams.useYearlyForDiscount;

  const ultimateOrPremium = isUltimate || isPremiumUser || isUltimateLaunch
  const currentPlan = ultimateOrPremium ? productData.ultimate : productData.premium

  const isDiscountPurchase = isLapsed || isPremiumUser || isUltimateLaunch

  if (isUltimate) {
    document.documentElement.setAttribute("paywall-style", "ultimate")
  }

  if (isPromo()) {
    document.documentElement.setAttribute("promo-type", "blackFriday")
  }

  const premiumPricing = PromoPricingInfo.fromYearlyAndMonthlyProduct(
    productData.premium.yearlyProduct,
    productData.premium.monthlyProduct,
    !useYearlyForDiscount,
    isDiscountPurchase
  )

  const ultimatePricing = PromoPricingInfo.fromYearlyAndMonthlyProduct(
    productData.ultimate.yearlyProduct,
    productData.ultimate.monthlyProduct,
    !useYearlyForDiscount,
    isDiscountPurchase
  )

  const currentPricing = isUltimate ? ultimatePricing : premiumPricing

  const state: State = {
    premiumPricing,
    ultimatePricing,
    currentPricing,
    isUltimate,
    isLapsed,
    isPremiumUser,
    isUltimateLaunch,
  }
  const actions = { openPaywallFromBanner, purchaseYearly, purchaseMonthly }

  return (
    <BlackFridayContext.Provider value={{ state, actions }}>{children}</BlackFridayContext.Provider>
  )
}

const useBlackFriday = () => {
  const context = useContext(BlackFridayContext)

  if (!context) {
    throw new Error("useBlackFriday has to be used within <BlackFridayContext.Provider>")
  }

  return context
}

export { BlackFridayProvider, BlackFridayContext, useBlackFriday }
